import * as pc from 'playcanvas'
import { registerCollider, type AABB } from '../colliders'

export type RenderPrimitive = 'box' | 'plane' | 'cylinder' | 'cone' | 'sphere'
export type Vec3Tuple = readonly [number, number, number]

export function createRenderEntity(options: {
  name: string
  type: RenderPrimitive
  position: Vec3Tuple
  scale: Vec3Tuple
  material: pc.Material
  parent: pc.Entity
  collider?: AABB
}): pc.Entity {
  const entity = new pc.Entity(options.name)
  entity.addComponent('render', { type: options.type })
  entity.setLocalPosition(...options.position)
  entity.setLocalScale(...options.scale)
  const instance = entity.render?.meshInstances[0]
  if (instance) instance.material = options.material
  options.parent.addChild(entity)
  if (options.collider) registerCollider(options.collider)
  return entity
}

export function escapeWorldHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

export interface OverlayHandle {
  element: HTMLDivElement
  dispose(): void
}

export function createWorldOverlay(
  app: pc.Application,
  style: Partial<CSSStyleDeclaration> = {},
): OverlayHandle {
  const element = document.createElement('div')
  element.dataset.arenaWorldOverlay = 'true'
  Object.assign(element.style, {
    position: 'absolute',
    inset: '0',
    pointerEvents: 'none',
    overflow: 'hidden',
    zIndex: '5',
    ...style,
  })
  const device = app.graphicsDevice as { canvas?: HTMLCanvasElement }
  device.canvas?.parentElement?.appendChild(element)
  let disposed = false
  return {
    element,
    dispose() {
      if (disposed) return
      disposed = true
      element.remove()
    },
  }
}

export interface WallSegment {
  id: string
  minX: number
  maxX: number
  minY: number
  maxY: number
  minZ: number
  maxZ: number
}

export function buildStandardInteriorShell(options: {
  root: pc.Entity
  prefix: string
  halfX: number
  halfZ: number
  height: number
  walls: readonly WallSegment[]
  floorColor: RgbTuple
  wallColor?: RgbTuple
  backdropColor: RgbTuple
  keyLightY: number
  keyLightRange: number
  accent: {
    name: string
    color: RgbTuple
    y: number
    z: number
    range: number
  }
}): void {
  const material = (
    color: RgbTuple,
    gloss: number,
    extra?: (value: pc.StandardMaterial) => void,
  ) => {
    const value = new pc.StandardMaterial()
    value.diffuse = new pc.Color(...color)
    value.gloss = gloss
    extra?.(value)
    value.update()
    return value
  }
  const floor = material(options.floorColor, 0.3)
  const cream = material(options.wallColor ?? [0.961, 0.922, 0.875], 0.15)
  const ceiling = material([0.961, 0.922, 0.875], 0.1, (value) => {
    value.cull = pc.CULLFACE_NONE
  })
  const add = (
    name: string,
    type: RenderPrimitive,
    position: Vec3Tuple,
    scale: Vec3Tuple,
    materialValue: pc.Material,
    collider?: AABB,
  ) => createRenderEntity({
    name,
    type,
    position,
    scale,
    material: materialValue,
    parent: options.root,
    collider,
  })

  add(
    `${options.prefix}-floor`,
    'plane',
    [0, 0, 0],
    [options.halfX * 2, 1, options.halfZ * 2],
    floor,
    {
      minX: -options.halfX,
      maxX: options.halfX,
      minY: -1,
      maxY: 0,
      minZ: -options.halfZ,
      maxZ: options.halfZ,
    },
  )
  for (const wall of options.walls) {
    add(
      wall.id,
      'box',
      [
        (wall.minX + wall.maxX) / 2,
        wall.minY + (wall.maxY - wall.minY) / 2,
        (wall.minZ + wall.maxZ) / 2,
      ],
      [
        wall.maxX - wall.minX,
        wall.maxY - wall.minY,
        wall.maxZ - wall.minZ,
      ],
      cream,
      { ...wall },
    )
  }
  add(
    `${options.prefix}-ceiling`,
    'plane',
    [0, options.height, 0],
    [options.halfX * 2, 1, options.halfZ * 2],
    ceiling,
  )
  buildExitAndBackdrop(add, options)
  addPointLight(
    options.root,
    `${options.prefix}-key-light`,
    [0.95, 0.93, 0.85],
    1.1,
    options.keyLightRange,
    [0, options.keyLightY, 0],
  )
  addPointLight(
    options.root,
    `${options.prefix}-${options.accent.name}`,
    options.accent.color,
    0.9,
    options.accent.range,
    [0, options.accent.y, options.accent.z],
  )
}

type RgbTuple = readonly [number, number, number]

function buildExitAndBackdrop(
  add: (
    name: string,
    type: RenderPrimitive,
    position: Vec3Tuple,
    scale: Vec3Tuple,
    material: pc.Material,
  ) => pc.Entity,
  options: {
    prefix: string
    halfX: number
    halfZ: number
    height: number
    backdropColor: RgbTuple
  },
): void {
  const frame = new pc.StandardMaterial()
  frame.diffuse = new pc.Color(0.1, 0.11, 0.13)
  frame.useMetalness = true
  frame.metalness = 0.5
  frame.gloss = 0.5
  frame.update()
  const glow = new pc.StandardMaterial()
  glow.diffuse = new pc.Color(0.1, 0.85, 0.45)
  glow.emissive = new pc.Color(0.2, 1, 0.55)
  glow.emissiveIntensity = 14
  glow.useLighting = false
  glow.update()
  const exitZ = options.halfZ - 0.08
  const doorHalf = 1.2
  const doorHeight = 2.4
  const jamb = 0.16
  for (const side of [-1, 1]) {
    add(
      `${options.prefix}-exit-jamb-${side}`,
      'box',
      [side * (doorHalf + jamb / 2), doorHeight / 2, exitZ],
      [jamb, doorHeight, 0.2],
      frame,
    )
  }
  add(
    `${options.prefix}-exit-head`,
    'box',
    [0, doorHeight + jamb / 2, exitZ],
    [doorHalf * 2 + jamb * 2, jamb, 0.2],
    frame,
  )
  add(
    `${options.prefix}-exit-sign`,
    'box',
    [0, doorHeight + 0.42, exitZ - 0.06],
    [0.9, 0.26, 0.06],
    glow,
  )

  const backdrop = new pc.StandardMaterial()
  backdrop.diffuse = new pc.Color(...options.backdropColor)
  backdrop.useLighting = false
  backdrop.cull = pc.CULLFACE_NONE
  backdrop.update()
  const pad = 6
  const height = options.height + 6
  const x = options.halfX + pad
  const z = options.halfZ + pad
  add(`${options.prefix}-backdrop-north`, 'box', [0, height / 2, -z], [x * 2, height, 0.4], backdrop)
  add(`${options.prefix}-backdrop-south`, 'box', [0, height / 2, z], [x * 2, height, 0.4], backdrop)
  add(`${options.prefix}-backdrop-west`, 'box', [-x, height / 2, 0], [0.4, height, z * 2], backdrop)
  add(`${options.prefix}-backdrop-east`, 'box', [x, height / 2, 0], [0.4, height, z * 2], backdrop)
  add(`${options.prefix}-backdrop-lid`, 'plane', [0, height, 0], [x * 2, 1, z * 2], backdrop)
  add(`${options.prefix}-backdrop-floor`, 'plane', [0, -0.05, 0], [x * 2, 1, z * 2], backdrop)
}

function addPointLight(
  root: pc.Entity,
  name: string,
  color: RgbTuple,
  intensity: number,
  range: number,
  position: Vec3Tuple,
): void {
  const light = new pc.Entity(name)
  light.addComponent('light', {
    type: 'point',
    color: new pc.Color(...color),
    intensity,
    range,
    castShadows: false,
  })
  light.setLocalPosition(...position)
  root.addChild(light)
}
